import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorHandler';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Eye, 
  EyeOff, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Send,
  Save 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBrevo({ config, setConfig, onSave, saving, testEmailAddress, setTestEmailAddress }) {
  const { t } = useLanguage();
  const [showPasswords, setShowPasswords] = useState({});
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);

  const toggleShowPassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error(t('enterTestEmail'));
      return;
    }
    
    setTestingEmail(true);
    setEmailTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/test-email`, { 
        test_email: testEmailAddress 
      });
      setEmailTestResult({ success: true, message: res.data.message });
      toast.success(t('emailTestSuccess'));
    } catch (err) {
      setEmailTestResult({ success: false, message: getErrorMessage(err, t('error')) });
      toast.error(t('emailTestFailed'));
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <div className="border border-border rounded-lg p-6 bg-card space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{t('emailConfiguration')}</h2>
          <p className="text-sm text-muted-foreground">{t('brevoSmtpSettings')}</p>
        </div>
        {emailTestResult && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${emailTestResult.success ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
            {emailTestResult.success ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {emailTestResult.success ? t('connected') : t('failed')}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('smtpHost')}</Label>
          <Input
            value={config.smtp_host}
            onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
            placeholder="smtp-relay.brevo.com"
            data-testid="smtp-host-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('smtpPort')}</Label>
          <Input
            value={config.smtp_port}
            onChange={(e) => setConfig({ ...config, smtp_port: e.target.value })}
            placeholder="587"
            data-testid="smtp-port-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('smtpUser')}</Label>
          <Input
            value={config.smtp_user}
            onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })}
            placeholder="your-smtp-user"
            data-testid="smtp-user-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('smtpPassword')}</Label>
          <div className="relative">
            <Input
              type={showPasswords.smtp_pass ? 'text' : 'password'}
              value={config.smtp_pass}
              onChange={(e) => setConfig({ ...config, smtp_pass: e.target.value, _smtp_pass_set: false })}
              placeholder={config._smtp_pass_set ? "••••••••  (déjà configuré)" : "Entrez le mot de passe"}
              data-testid="smtp-pass-input"
              className={config._smtp_pass_set && !config.smtp_pass ? "border-green-500" : ""}
            />
            <button
              type="button"
              onClick={() => toggleShowPassword('smtp_pass')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPasswords.smtp_pass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {config._smtp_pass_set && !config.smtp_pass && (
            <p className="text-xs text-green-600">✓ Mot de passe déjà configuré (laissez vide pour conserver)</p>
          )}
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>{t('emailFrom')}</Label>
          <Input
            type="email"
            value={config.email_from}
            onChange={(e) => setConfig({ ...config, email_from: e.target.value })}
            placeholder="noreply@vitalis-sports.ch"
            data-testid="email-from-input"
          />
        </div>
      </div>

      {/* Test Email Section */}
      <div className="border-t border-border pt-4 space-y-4">
        <h3 className="text-lg font-medium">{t('testEmailConnection')}</h3>
        <div className="flex gap-3 items-end">
          <div className="flex-1 space-y-2">
            <Label>{t('testEmailAddress')}</Label>
            <Input
              type="email"
              value={testEmailAddress}
              onChange={(e) => setTestEmailAddress(e.target.value)}
              placeholder="test@example.com"
              data-testid="test-email-input"
            />
          </div>
          <Button
            variant="secondary"
            onClick={handleTestEmail}
            disabled={testingEmail || !testEmailAddress}
            data-testid="send-test-email-btn"
          >
            {testingEmail ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {testingEmail ? t('sending') : t('sendTestEmail')}
          </Button>
        </div>
        {emailTestResult && (
          <p className={`text-sm ${emailTestResult.success ? 'text-emerald-600' : 'text-red-600'}`}>
            {emailTestResult.message}
          </p>
        )}
      </div>

      <div className="flex gap-3 pt-4 border-t border-border">
        <Button
          onClick={() => onSave('email')}
          disabled={saving}
          data-testid="save-email-config-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? t('saving') : t('save')}
        </Button>
      </div>
    </div>
  );
}
